import { useNavigate, useParams } from 'react-router-dom';

import { Tooltip } from '@mui/material';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import { DATE_TIME_FORMAT_SHORTER } from 'constants/constants';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import { permissionsUtil } from 'utils/permissionsUtil';

import Chip from 'components/Chip/Chip';
import ActionCell from 'components/DataTableComponent/components/ActionCell';

import {
  BillingPeriodUnit,
  GetPlanResponse,
  Plan,
  PlanStatus
} from '../../../common/contracts';

export function usePlansColumns(
  archivePlan: UseMutationResult<Plan, Error, string, unknown>,
  getPlans: UseQueryResult<GetPlanResponse, unknown>
) {
  const navigate = useNavigate();
  const { publisherId } = useParams();

  const columns: ColumnDef<any>[] = [
    { accessorKey: 'name', header: 'Name' },
    { accessorKey: 'displayName', header: 'Display Name' },
    {
      accessorKey: 'updatedAt',
      header: 'Last Update',
      cell: (cell: CellContext<any, unknown>) => {
        const updatedAt = cell.getValue() as string;
        return updatedAt
          ? dayjs(updatedAt).utc().format(DATE_TIME_FORMAT_SHORTER)
          : '';
      }
    },
    {
      accessorKey: 'billingPeriodUnit',
      header: 'Billing Period',
      cell: (cell: CellContext<any, unknown>) => {
        const billingPeriodUnit = cell.getValue() as BillingPeriodUnit;
        switch (billingPeriodUnit) {
          case BillingPeriodUnit.DAILY:
            return 'Daily';
          case BillingPeriodUnit.WEEKLY:
            return 'Weekly';
          case BillingPeriodUnit.MONTHLY:
            return 'Monthly';
          case BillingPeriodUnit.YEARLY:
            return 'Yearly';
        }
      }
    },
    {
      header: 'Base Price',
      cell: (cell: CellContext<any, unknown>) => {
        const basePriceInUsd = cell.row.original.basePriceInUsd as number;
        const numberWithFraction = new Decimal(basePriceInUsd)
          .div(100)
          .toNumber();
        return (
          <Tooltip arrow title={basePriceInUsd ? `$${numberWithFraction}` : ''}>
            <span>{basePriceInUsd ? `$${numberWithFraction}` : ''}</span>
          </Tooltip>
        );
      }
    },
    { accessorKey: 'billingCycles', header: 'Billing Cycles' },
    {
      accessorKey: 'coupon.discountPercentage',
      header: 'First Payment Discount',
      cell: (cell: CellContext<any, unknown>) => {
        const discountPercentage = cell.getValue() as number;
        return discountPercentage ? `${discountPercentage}%` : '-';
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (cell: CellContext<any, unknown>) => {
        const status = cell.getValue() as string;
        const chipStyle = status === PlanStatus.ACTIVE ? 'success' : 'danger';
        const capitalizedStatus =
          status.charAt(0).toUpperCase() + status.slice(1);

        return (
          <Chip variant="flat" status={chipStyle}>
            {capitalizedStatus}
          </Chip>
        );
      }
    },
    {
      accessorKey: 'actions',
      header: '',
      maxSize: 5,
      size: 5,
      cell: (cell: CellContext<any, unknown>) => {
        const { original } = cell.row;

        const isActionEnabled = permissionsUtil.isActionEnabled(
          null,
          EPermissionGroup.SUBSCRIPTIONS,
          EPermissionAction.MANAGE,
          publisherId
        );
        return (
          <ActionCell
            rowData={original}
            actions={[
              {
                label: 'Edit',
                disabled: !isActionEnabled,
                handler: () => {
                  navigate(`./form/${original._id}`);
                }
              },
              {
                label: 'Archive',
                disabled: !isActionEnabled,
                handler: async () => {
                  await archivePlan.mutateAsync(original._id, {
                    onSuccess: () => {
                      getPlans.refetch();
                    }
                  });
                }
              }
            ]}
          />
        );
      }
    }
  ];

  return { columns };
}
